import { asImageSrc } from '@prismicio/client';

import type { SiteSettings } from './site-settings';
import type { ImageField, KeyTextField } from '@prismicio/client';

/**
 * The tags `metaFor` produces. A narrower type than React Router's
 * `MetaDescriptor` (which it is assignable to) so a caller — or a test — can
 * narrow on `title`, `name` or `property` without a cast.
 */
export type MetaTag =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string }
  | { tagName: 'link'; rel: 'canonical'; href: string };

/**
 * The SEO & Metadata tab every Prismic type in this repo shares. Typed
 * structurally so any document can be passed without a union of nine types.
 */
export type MetaDocument = {
  data: {
    meta_title?: KeyTextField;
    meta_description?: KeyTextField;
    meta_image?: ImageField;
  };
};

export type MetaRoute = {
  siteSettings: SiteSettings;
  /** The route's own title, used when the document has no `meta_title`. */
  title?: string;
  description?: string;
  /** Path from the site root, e.g. `/about`. Produces the canonical link. */
  path?: string;
};

const filled = (value: string | null | undefined) => {
  const trimmed = value?.trim();
  return trimmed && trimmed !== '' ? trimmed : undefined;
};

/**
 * Merges `site_settings` SEO defaults with a route's own values and a
 * document's `meta_*` fields — document wins, then route, then defaults — and
 * returns the descriptors a React Router `meta` export needs.
 */
export function metaFor(route: MetaRoute, doc?: MetaDocument | null): Array<MetaTag> {
  const { seo } = route.siteSettings;

  const pageTitle = filled(doc?.data.meta_title) ?? filled(route.title);
  const title = pageTitle ? seo.titleTemplate.replace('%s', pageTitle) : seo.defaultTitle;

  const description = filled(doc?.data.meta_description) ?? filled(route.description) ?? seo.defaultDescription;

  const image = (doc?.data.meta_image ? asImageSrc(doc.data.meta_image) : null) ?? seo.ogImageUrl;

  const canonical = seo.canonicalBase && route.path ? `${seo.canonicalBase}${route.path}` : undefined;

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: canonical && image.startsWith('/') ? `${seo.canonicalBase}${image}` : image },
    { property: 'og:type', content: 'website' },
    ...(canonical
      ? ([
          { property: 'og:url', content: canonical },
          { tagName: 'link', rel: 'canonical', href: canonical },
        ] satisfies Array<MetaTag>)
      : []),
  ];
}
